import { useState, useCallback, useMemo } from "react";
import {
  ContextualSaveBar,
  Page,
  Layout,
  BlockStack,
  Loading,
  Banner,
  Text,
  Box,
} from "@medentic/polaris-react";
import { ExternalIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { AppPageWrapper } from "@shared/ui";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Brand, GeoAddress } from "@features/events/types";
import usePageMeta from "@shared/hooks/common/usePageMeta";
import { validateMyBrandRequiredFields, clearBrandFieldError } from "@shared/lib";
import { getLocalizedText } from "@shared/lib";
import { showToast } from "@shared/lib";
import { RootState } from "@app/store/store";
import { useMyBrand } from "@features/events/hooks/useMyBrand";
import TranslationsCard, { TranslationsEntityType } from "@features/events/components/TranslationsCard";

import {
  OverviewCard,
  ContactInformationCard,
  SocialMediaProfilesCard,
  BrandingMediaCard,
  BillingEmailCard,
} from "./components";
import StatusCard from "@features/events/components/StatusCard";
import { StatusEntityType } from "@features/events/types";

// Helper to sanitize GeoAddress by removing any bounds property from API response
const sanitizeGeoAddress = (geoAddress: GeoAddress | undefined): GeoAddress | undefined => {
  if (!geoAddress) return undefined;
  const { bounds: _bounds, ...rest } = geoAddress as GeoAddress & {
    bounds?: unknown;
  };
  void _bounds;
  return rest as GeoAddress;
};

const mapMyBrandToDraftData = (myBrand: Brand | undefined): Partial<Brand> => {
  if (!myBrand) {
    return {};
  }

  return {
    id: myBrand.id,
    slug: myBrand.slug,
    name: myBrand.name,
    headline: myBrand.headline,
    description: myBrand.description,
    visibility: myBrand.visibility,
    verified: myBrand.verified,
    logoRectangle: myBrand.logoRectangle,
    logoSquare: myBrand.logoSquare,
    website: myBrand.website,
    social: myBrand.social,
    contactEmail: myBrand.contactEmail,
    contactPhone: myBrand.contactPhone,
    contactPhoneCountryCode: myBrand.contactPhoneCountryCode,
    billingEmail: myBrand.billingEmail,
    languageCodes: myBrand.languageCodes,
    geoAddress: sanitizeGeoAddress(myBrand.geoAddress),
    customButtonLabel: myBrand.customButtonLabel,
    customButtonLink: myBrand.customButtonLink,
    link: myBrand.link,
  };
};

export default function BrandProfile() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{
    eventOrganizerId: string;
  }>();
  const { PUT } = useApiRequest();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const selectedLanguages = useSelector(
    (state: RootState) => state.event.selectedLanguages
  );

  // State
  const [brandDataDraft, setBrandDataDraft] = useState<Partial<Brand> | null>(
    null
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [translationsExpanded, setTranslationsExpanded] = useState(false);

  // Fetch brand data using the useMyBrand hook
  const {
    myBrand,
    isLoading,
    isFetching,
    refetchMyBrand,
  } = useMyBrand(hcc);

  // Prepare page meta data
  const brandName = getLocalizedText(myBrand?.name);
  const brandLogoUrl = myBrand?.logoSquare?.[0]?.src || myBrand?.logoRectangle?.[0]?.src;

  // Update page meta when brand data is available
  usePageMeta(brandName, brandLogoUrl);

  const initialBrandData = useMemo(
    () => mapMyBrandToDraftData(myBrand),
    [myBrand]
  );
  const brandData = brandDataDraft ?? initialBrandData;

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: Partial<Brand>) => {
      return PUT(
        `/api/healthcare-companies/${hcc}/e/me`,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["my-brand", hcc],
      });
      refetchMyBrand();
      setBrandDataDraft(null);
      setIsDirty(false);
      setFieldErrors({});
      dispatch(
        showToast({
          message: t("brandProfile.saveSuccess"),
          type: "success",
        })
      );
    },
    onError: (error: unknown) => {
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: { data?: { errors?: Record<string, string> } };
        };
        if (apiError.response?.data?.errors) {
          setFieldErrors(apiError.response.data.errors);
        }
      }
      dispatch(
        showToast({
          message: t("brandProfile.saveError"),
          type: "error",
        })
      );
    },
  });

  // Handle data changes with centralized real-time error clearing
  const handleDataChange = useCallback(
    (updates: Partial<Brand>) => {
      const newBrandData = { ...brandData, ...updates };
      setBrandDataDraft(newBrandData);
      setIsDirty(true);

      // Use centralized error clearing helper
      setFieldErrors((prevErrors) => {
        let newErrors = { ...prevErrors };
        Object.entries(updates).forEach(([field, value]) => {
          newErrors = clearBrandFieldError(
            field,
            value,
            newErrors,
            selectedLanguages,
            t
          );
        });
        return newErrors;
      });
    },
    [brandData, selectedLanguages, t]
  );

  // Handle save
  const handleSave = useCallback(() => {
    // Validate required fields before saving
    const validationResult = validateMyBrandRequiredFields(
      brandData,
      t,
      selectedLanguages
    );

    if (!validationResult.isValid) {
      // Set field errors
      setFieldErrors(validationResult.errors);

      // Show toast notification
      const errorCount = Object.keys(validationResult.errors).length;
      const translationKey =
        errorCount === 1
          ? "common.validation.requiredFieldsError_one"
          : "common.validation.requiredFieldsError_other";

      dispatch(
        showToast({
          message: t(translationKey, {
            count: errorCount,
            defaultValue: `Please fill in all required fields. ${errorCount} field${
              errorCount > 1 ? "s" : ""
            } need${errorCount > 1 ? "" : "s"} attention.`,
          }),
          type: "error",
        })
      );
      return;
    }

    // Filter out undefined values and clean the data before sending
    const cleanData = Object.entries(brandData).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          // Special handling for empty strings in optional fields
          if (key === "customButtonLink" && value === "") {
            // Don't include empty customButtonLink
            return acc;
          }
          // Don't send id, link, createdAt, updatedAt
          if (!["id", "link", "createdAt", "updatedAt", "isMy", "isUsedBefore", "role"].includes(key)) {
            (acc as Record<string, unknown>)[key] = value;
          }
        }
        return acc;
      },
      {} as Partial<Brand>
    );

    saveMutation.mutate(cleanData);
  }, [brandData, saveMutation, t, selectedLanguages, dispatch]);

  // Handle discard changes
  const handleDiscard = useCallback(() => {
    if (myBrand) {
      setBrandDataDraft(null);
      setIsDirty(false);
      setFieldErrors({});
    }
  }, [myBrand]);

  const handlePreview = useCallback(() => {
    if (brandData.link) {
      window.open(brandData.link, "_blank");
    }
  }, [brandData.link]);

  if (isLoading) {
    return (
      <AppPageWrapper>
        <Page title={t("brandProfile.title")}>
          <Loading />
        </Page>
      </AppPageWrapper>
    );
  }

  return (
    <AppPageWrapper>
      {isDirty && (
        <ContextualSaveBar
          backgroundColor="#2B0069"
          saveAction={{
            content: t("common.save"),
            onAction: handleSave,
            loading: saveMutation.isPending,
          }}
          discardAction={{
            onAction: handleDiscard,
          }}
        />
      )}

      {isFetching && <Loading />}

      <Page
        fullWidth
        title={brandName || t("brandProfile.title")}
        primaryAction={{
          content: t("brandProfile.actions.preview"),
          icon: ExternalIcon,
          onAction: handlePreview,
        }}
      >
        {Object.keys(fieldErrors).length > 0 && (
          <Box paddingBlockEnd="400">
            <Banner tone="critical" title={t("common.validation.errorsPresent")}>
              <BlockStack gap="200">
                {Object.entries(fieldErrors).map(([, error], index) => (
                  <Text key={index} as="p">
                    {error}
                  </Text>
                ))}
              </BlockStack>
            </Banner>
          </Box>
        )}

        <Layout>
          <Layout.Section>
            <BlockStack gap="400">
              <OverviewCard
                brandData={JSON.parse(JSON.stringify(brandData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
                hcc={hcc}
              />

              <BrandingMediaCard
                brandData={JSON.parse(JSON.stringify(brandData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <ContactInformationCard
                brandData={JSON.parse(JSON.stringify(brandData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <SocialMediaProfilesCard
                brandData={JSON.parse(JSON.stringify(brandData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <BillingEmailCard
                brandData={JSON.parse(JSON.stringify(brandData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />
            </BlockStack>
          </Layout.Section>

          <Layout.Section variant="oneThird">
            <Layout>
              <Layout.Section>
                <StatusCard 
                  entityType={StatusEntityType.BRAND}
                  featured={null}
                  verified={brandData.verified ?? false}
                  visibility={brandData.visibility ?? 0}
                  onDataChange={handleDataChange} 
                />
              </Layout.Section>

              <Layout.Section>
                <TranslationsCard
                  translationsExpanded={translationsExpanded}
                  setTranslationsExpanded={setTranslationsExpanded}
                  entityType={TranslationsEntityType.INSTRUCTOR}
                />
              </Layout.Section>
            </Layout>
          </Layout.Section>
        </Layout>
      </Page>
    </AppPageWrapper>
  );
}
